import type { ReactElement, ReactNode } from 'react'

import { FlowPage } from '@monthio/design-system'
import {
	eventHubService,
	flowAnimationService,
	FlowContainer,
	LoadingScreen,
	trackingService,
} from '@monthio/react-components'
import { setContext, startSpan, startSpanManual } from '@sentry/react'
import { GeneralErrorPage } from '#components/GeneralErrorPage/index'
import {
	createContext,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import type { ModuleDefinitionType } from '../types/authenticateSmartcheckSessionDto'

import { ChunkLoadErrorBoundary } from '../utils/ChunkLoadingErrorBoundary'

export interface ModuleData<Start, Finish, Config> {
	start: Start
	finish: Finish
	config: Config
}

interface ModuleContextValue<M extends ModuleData<any, any, any>> {
	data: M['start']
	finish: (data: M['finish']) => Promise<void>
	config: M['config']
}

interface ModuleSettings {
	handleOwnLoadingStates: boolean
}

const defaultModuleSettings: ModuleSettings = {
	handleOwnLoadingStates: false,
}

export const ModuleContext = createContext<ModuleContextValue<any> | undefined>(
	undefined,
)

export interface ProviderProps<M extends ModuleData<any, any, any>> {
	children: ReactNode
	moduleType: ModuleDefinitionType
	config: M['config']
	startCall: () => Promise<M['start']>
	finishCall: (data: M['finish']) => Promise<void>
	moduleSettings?: ModuleSettings
	isDev?: boolean
}

const ModuleLoading = () => (
	<FlowPage>
		<FlowPage.Content $isCentered>
			<LoadingScreen />
		</FlowPage.Content>
	</FlowPage>
)

export const ModuleProvider = <M extends ModuleData<any, any, any>>(
	props: ProviderProps<M>,
): ReactElement => {
	useEffect(() => {
		const spanFinish = startSpanManual(
			{ name: `ModuleProvider-${props.moduleType}`, op: 'ui.react.render' },
			(span) => {
				return () => span.end()
			},
		)

		return () => {
			spanFinish()
		}
	}, [props.moduleType])

	return (
		<ChunkLoadErrorBoundary moduleFlag={props.moduleType}>
			<Suspense fallback={<ModuleLoading />}>
				<ModuleProviderImplementation key={props.moduleType} {...props} />
			</Suspense>
		</ChunkLoadErrorBoundary>
	)
}

const ModuleProviderImplementation = <M extends ModuleData<any, any, any>>({
	children,
	moduleType,
	config,
	startCall,
	finishCall,
	moduleSettings = defaultModuleSettings,
	isDev = false,
}: ProviderProps<M>): ReactElement => {
	const { t } = useTranslation()

	const [data, setData] = useState<M['start']>()
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<unknown>()

	const moduleConfig = useMemo(() => config, [JSON.stringify(config)])

	useEffect(() => {
		setContext('module-config', config)
	}, [config])

	useEffect(() => {
		let mounted = true
		const start = async () => {
			try {
				const d = await startSpan({ name: `ModuleProviderStartCall` }, () =>
					startCall(),
				)
				if (mounted) {
					setData(d)
					setLoading(false)
				}
			} catch (error_) {
				if (mounted) {
					setError(error_)
					setLoading(false)
				}
			}
		}

		start()
		flowAnimationService.showAnimation = false
		return () => {
			mounted = false
		}
	}, [startCall])

	const finish = useCallback(
		async (payload: M['finish']) => {
			setSaving(true)
			try {
				await startSpan({ name: `ModuleProviderFinishCall` }, () =>
					finishCall(payload),
				)
				if (!isDev) {
					eventHubService.onEvent({
						source: 'budgetConsumerClient',
						topic: 'moduleCompleted',
						payload: { module: moduleType },
					})
				}
			} catch (error_) {
				trackingService.trackError(error_ as any, 'RouteProviderFinishCall')
				if (moduleSettings.handleOwnLoadingStates) {
					throw error_
				}

				setError(error_)
				setSaving(false)
			}
		},
		[finishCall, moduleType, isDev, moduleSettings.handleOwnLoadingStates],
	)

	const value = useMemo(
		() => ({
			data,
			finish,
			config: moduleConfig,
		}),
		[data, finish, moduleConfig],
	)

	if (error && moduleSettings.handleOwnLoadingStates == false) {
		return <GeneralErrorPage />
	}

	if (
		moduleSettings.handleOwnLoadingStates == false &&
		(loading || saving || data == null)
	) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen
						title={
							loading
								? t('miniBudgetForm-loadingScreen-retrievingData-text')
								: undefined
						}
					/>
				</FlowPage.Content>
			</FlowPage>
		)
	}

	return (
		<ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>
	)
}

export const useModule = <
	M extends ModuleData<any, any, any>,
>(): ModuleContextValue<M> => {
	const ctx = useContext<ModuleContextValue<M> | undefined>(ModuleContext)
	if (ctx == null) {
		throw Error('useModule must be used inside a ModuleProvider')
	}
	return ctx
}
